import React, { useState, useEffect, useMemo } from "react";
import { Select, Button, Input, Dropdown, Checkbox } from "antd";
import { ProfileLevels } from "./ViewUser/types";
import ViewUser from "./ViewUser";
import LogoAvatar from "../../assets/logoAvatar.svg";
import "./overrides.scss";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_USER,
  DELETE_USER_BY_ID,
  GET_ALL_USERS,
  SET_USER_MANAGER,
  UPDATE_USER_BY_ID,
} from "../../api/graphql/users";
import { Table } from "antd";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import HorizontalDots from "../../assets/horizontalDots.svg";
import Cookies from "js-cookie";
import { AddUserModal } from "./UsersModal/AddUserModal/AddUserModal";
import EditUserModal from "./UsersModal/EditUserModal/EditUserModal";
import DeleteUserModal from "../DeleteModal/DeleteUserModal";
import BasicButton from "../../BasicButton";
import { login } from "../../utils/user";
import Page from "../Page";
import { SearchInput, Toolbar, filterStyles } from "../Filters";
import styles from "./styles.module.scss";
import { useUser } from "../../context/UserContext";
import ManagerSelect, { Manager } from "../ManagerSelect";
import UserComment, { AdminComment } from "../UserComment";
import ModerationSwitch from "../Moderation/ModerationSwitch";
import useManualModeration from "../Moderation/useManualModeration";
import useInfiniteRows from "../../hooks/useInfiniteRows";
import useIsPhone from "../../hooks/useIsPhone";
import DataCards, { CardColumn } from "../DataTable/DataCards";
import { isInternalUser } from "../../utils/internalUsers";

const { Option } = Select;

interface AppData {
  name: string;
  platform: string;
  country: string;
  ratings: number;
  reviews: number;
  version: string;
  lastUpdate: string;
  size: number;
}

export interface User {
  timezone: string;
  id: number;
  name: string;
  email: string;
  createdAt: string;
  lastActivity: string;
  status: string;
  avatarUrl: string;
  apps?: AppData[];
  password: string;
  profileLevel: ProfileLevels;
  totalSpendings: number;
  balance: number;
  discount: number;
  telegram: string;
  phone: string;
  manager?: Manager | null;
  adminComment?: AdminComment | null;
}

export const formatDate = (date: Date | string, separationFormat = ".") => {
  let d: any;

  if (Number(date) > 1000000000000) {
    d = dayjs.unix(Number(date) / 1000);
  } else {
    d = dayjs(date);
  }

  if (!d.isValid()) return "";

  // Return formatted date
  return d.format(`YYYY${separationFormat}MM${separationFormat}DD`);
};

const UsersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("All statuses");
  const [showInternal, setShowInternal] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newUser, setNewUser] = useState<Partial<User>>({
    name: "",
    email: "",
    phone: "",
    // status: "",
    password: "",
  });
  const [updateUserById] = useMutation(UPDATE_USER_BY_ID);
  const [deleteUserById] = useMutation(DELETE_USER_BY_ID);
  const [createUser] = useMutation(CREATE_USER);
  // Written to the cache, so the row shows the new manager after a refilter.
  const [setUserManager] = useMutation(SET_USER_MANAGER, {
    fetchPolicy: "network-only",
  });
  const { user: currentUser } = useUser();
  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const { data, refetch } = useQuery(GET_ALL_USERS, {
    fetchPolicy: "network-only",
  });
  const moderation = useManualModeration();
  const isPhone = useIsPhone();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [editingPhone, setEditingPhone] = useState<{
    id: number | null;
    value: string;
  }>({ id: null, value: "" });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEditPhone = (userId: number, phone: string) => {
    setEditingPhone({ id: userId, value: phone });
    console.log(phone);
  };

  const handleSavePhone = (userId: number) => {
    const updatedUsers = users.map((user) =>
      user.id === userId ? { ...user, phone: editingPhone.value } : user,
    );
    setUsers(updatedUsers);
    console.log(updatedUsers);
    setEditingPhone({ id: null, value: "" });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingPhone((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleEditUser = (user: User) => {
    setEditedUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditedUser(null);
  };

  const handleSaveUser = async (updatedUser: {
    name: string;
    phone: string;
    email: string;
    timezone: string;
  }) => {
    if (editedUser) {
      const updatedUsers = users.map((user) =>
        user.id === editedUser.id ? { ...user, ...updatedUser } : user,
      );
      console.log(updatedUser, editedUser);
      await updateUserById({
        variables: {
          userId: editedUser.id,
          updateUser: updatedUser,
        },
      }).then(() =>
        refetch().then(({ data }: any) =>
          handleUserClick(
            data?.getAllUsers.find(
              (user: User) => user.id === editedUser?.id,
            ),
          ),
        ),
      );
      setUsers(updatedUsers);
      setSelectedUser({ ...editedUser, ...updatedUser });
      setEditedUser(null);
    }
    setIsEditModalOpen(false);
  };

  const handleRefetchWithSelectedUser = () => {
    refetch().then(({ data }: any) => {
      console.log("data", data);
      handleUserClick(
        data?.getAllUsers?.find((user: User) => user.id === selectedUser?.id),
      );
    });
  };

  const handleAddUser = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewUser({
      name: "",
      email: "",
      phone: "",
      // status: "",
      password: "",
    });
  };

  const handleUserClick = (user: User) => {
    navigate(`/user/${user.id}`);
    // setSelectedUser({ ...user, apps: mockApps });
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, phone, password } = newUser;
    const userInput = { name, email, phone, password };
    await createUser({
      variables: {
        user: userInput,
      },
    });
    refetch();
    handleModalClose();
  };

  const mockApps: AppData[] = [
    {
      name: "App 1",
      platform: "iOS",
      country: "US",
      ratings: 4.5,
      reviews: 120,
      version: "1.2.0",
      lastUpdate: "2024-08-01",
      size: 50,
    },
    {
      name: "App 2",
      platform: "Android",
      country: "UK",
      ratings: 4.8,
      reviews: 90,
      version: "1.3.0",
      lastUpdate: "2024-07-15",
      size: 40,
    },
  ];

  // Cells shared by the table and a phone's cards.
  const renderName = (user: User) => (
    <span
      className={styles.nameCell}
      onClick={() => handleUserClick(user)}
      role="button"
      tabIndex={0}
      onKeyPress={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          handleUserClick(user);
        }
      }}
    >
      <img src={LogoAvatar} alt={user.name} className={styles.avatar} />
      <span className={styles.link}>{user.name}</span>
    </span>
  );

  const renderModeration = (user: User) => (
    <ModerationSwitch
      checked={moderation.isManual(user.id)}
      disabled={moderation.saving}
      ariaLabel={`Manual moderation of ${user.email}'s orders`}
      onToggle={() => moderation.toggle(user.id)}
    />
  );

  const renderManager = (user: User) => (
    <ManagerSelect
      value={user.manager ?? null}
      onChange={(manager) =>
        setUserManager({
          variables: {
            userId: user.id,
            managerId: manager?.id ?? null,
          },
        })
      }
    />
  );

  const renderComment = (user: User) => (
    <UserComment
      userId={user.id}
      userLabel={user.email || user.name || `user #${user.id}`}
      value={user.adminComment}
    />
  );

  const renderActions = (user: User) => (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        items: [
          { key: "edit", label: "Edit", onClick: () => handleEditUser(user) },
          { key: "view", label: "View", onClick: () => handleUserClick(user) },
          { key: "login", label: "Login", onClick: () => login(user.id) },
        ],
      }}
    >
      <button className={styles.dotsButton} aria-label="Actions">
        <img src={HorizontalDots} alt="" width={20} height={20} />
      </button>
    </Dropdown>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: string, user: User) => renderName(user),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (text: string, user: User) =>
        editingPhone.id === user.id ? (
          <span className={styles.phoneEdit}>
            <Input
              size="small"
              value={editingPhone.value}
              onChange={handlePhoneChange}
              className={styles.phoneInput}
            />
            <Button
              size="small"
              type="primary"
              onClick={() => handleSavePhone(user.id)}
            >
              Save
            </Button>
          </span>
        ) : (
          <span
            onClick={() => handleEditPhone(user.id, text)}
            style={{ cursor: "pointer" }}
          >
            {text}
          </span>
        ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text: string, user: User) => (
        <span
          onClick={() => handleUserClick(user)}
          className={styles.link}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => formatDate(text),
    },
    {
      title: "Last Activity",
      dataIndex: "lastActivity",
      key: "lastActivity",
      render: (text: string) => formatDate(text)
    },
    // On: the user's orders wait for an admin on the Moderation page. Left
    // out while the backend has no moderation.
    ...(moderation.available
      ? [
          {
            title: "Manual moderation",
            key: "orderModeration",
            width: 168,
            render: (_: any, user: User) => renderModeration(user),
          },
        ]
      : []),
    // Only a super admin sees and assigns the client's manager.
    ...(isSuperAdmin
      ? [
          {
            title: "Manager",
            key: "manager",
            width: 200,
            render: (_: any, user: User) => renderManager(user),
          },
        ]
      : []),
    // Admins' note on the client; the client never sees it.
    {
      title: "Comment",
      key: "adminComment",
      width: 260,
      render: (_: any, user: User) => renderComment(user),
    },
    {
      title: "",
      key: "action",
      width: 20,
      render: (_: any, user: User) => renderActions(user),
    },
  ];

  // A phone: a card per user, the name heading it and the menu beside it.
  const cardColumns: CardColumn<User>[] = [
    { key: "name", title: "Name", mobile: "title", render: renderName },
    { key: "action", title: "", mobile: "header", render: renderActions },
    {
      key: "email",
      title: "Email",
      mobile: "full",
      render: (user) => (
        <span onClick={() => handleUserClick(user)} className={styles.link}>
          {user.email}
        </span>
      ),
    },
    { key: "phone", title: "Phone", render: (user) => user.phone },
    {
      key: "createdAt",
      title: "Created",
      render: (user) => formatDate(user.createdAt),
    },
    {
      key: "lastActivity",
      title: "Last activity",
      render: (user) => formatDate(user.lastActivity),
    },
    ...(moderation.available
      ? [
          {
            key: "orderModeration",
            title: "Moderation",
            render: renderModeration,
          },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          {
            key: "manager",
            title: "Manager",
            mobile: "full" as const,
            render: renderManager,
          },
        ]
      : []),
    {
      key: "adminComment",
      title: "Comment",
      mobile: "full",
      render: renderComment,
    },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRemoveUser = async (userId: number) => {
    setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userId));
    console.log(users);
    setOpenDropdown(null);
    await deleteUserById({
      variables: {
        userId,
      },
    });
  };

  const handleDeleteUser = async () => {
    if (!editedUser) return;
    try {
      await deleteUserById({
        variables: {
          userId: editedUser?.id,
        },
      });

      setEditedUser(null);
      setIsDeleteModalOpen(false);
      setIsEditModalOpen(false);
      await refetch();
      if (location.pathname.startsWith('/user/')) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setIsDeleteModalOpen(false);
      setIsEditModalOpen(false);
    }
  };

  const DeleteDescription = useMemo(
    () => (
      <p className="deleteP">
        Are you sure you want to delete user
        <br /> <span>ID:{editedUser?.id}</span>?
      </p>
    ),
    [editedUser?.id],
  );

  useEffect(() => {
    if (data?.getAllUsers) {
      setFilteredUsers(
        data?.getAllUsers?.filter(
          (user: User) =>
            // Internal (test) accounts stay out unless asked for
            (showInternal || !isInternalUser(user)) &&
            (selectedStatus === "All statuses" ||
              user?.status === selectedStatus) &&
            (user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user?.phone?.includes(searchTerm)),
        ),
      );
    }
  }, [data?.getAllUsers, searchTerm, showInternal]);

  const { visibleRows: visibleUsers, sentinelRef } = useInfiniteRows(
    filteredUsers,
    JSON.stringify([searchTerm, showInternal]),
  );

  return (
    <div>
      <Page
        title="Users"
        actions={
          <BasicButton onClick={handleAddUser} title="+ Add User" variant="primary" />
        }
      >
        <Toolbar>
          <SearchInput
            placeholder="Search user"
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <Checkbox
            checked={showInternal}
            onChange={(e) => setShowInternal(e.target.checked)}
            className={filterStyles.checkbox}
          >
            Show internal users
          </Checkbox>
        </Toolbar>
        {isPhone ? (
          <DataCards
            columns={cardColumns}
            rows={visibleUsers}
            rowKey={(user) => user.id}
            emptyText="No users"
            sentinelRef={sentinelRef}
          />
        ) : (
          <div>
            <Table
              rowSelection={{
                type: "checkbox",
              }}
              dataSource={visibleUsers}
              columns={columns}
              rowKey="id"
              pagination={false}
            />
            <div ref={sentinelRef} aria-hidden />
          </div>
        )}
      </Page>
      <AddUserModal
        isOpen={isModalOpen}
        newUser={newUser}
        handleInputChange={handleInputChange}
        handleModalClose={handleModalClose}
        handleSubmit={handleSubmit}
      />
      <EditUserModal
        user={editedUser}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        handleDelete={() => setIsDeleteModalOpen(true)}
        onSave={handleSaveUser}
      >
        <DeleteUserModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          handleDelete={handleDeleteUser}
          title="Delete User"
          description={DeleteDescription}
          confirmText="Delete"
          cancelText="Cancel"
        />
      </EditUserModal>
    </div>
  );
};

export default UsersPage;
